import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import './index.scss';
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from 'react';
import useIsMounted from "../../base/ui/hooks/use-is-mounted";
import MapboxApiService, {GetMapboxMarkerAddressResponseDS} from "../core/services/api";
import classnames from "classnames";
import {ErrorBoundary} from "react-error-boundary";
import debounce from "lodash.debounce";
import {Box, BoxProps} from "@mui/material";
import {getCountryCode} from "../core/models/country-codes";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import MapboxCircles, {MapboxCircle} from "./circles";
import MapboxInteractions from "./interactions";
import MapboxMarkers, {MapboxMarker} from "./markers";

export interface MapboxAddress {
    country: string,
    countryShortCode?: string;
    city?: string;
    address: string,
    province: string,
    postalCode: string,
}

export interface MapboxStartingAddress {
    address: string,
    country?: string,
}

export type MapboxCoordinates = { lng: number, lat: number };


export interface MapboxControls {
    navigation?: boolean;
    geolocate?: boolean;
    geocoder?: boolean;
    scale?: boolean;
}

export interface MapboxOptions extends Omit<mapboxgl.MapOptions, 'style' | 'container'> {
    theme: 'light' | 'dark';
}

export interface MapboxProps {
    /**
     * Callback function that is called when a marker is clicked on the mapbox
     * @param address
     */
    onAddressSelect?: (address: MapboxAddress) => void;
    /**
     * Callback function that is called when a marker is clicked on the mapbox
     * @param coordinates
     */
    onCoordinatesSelect?: (coordinates: MapboxCoordinates) => void;
    /**
     * Callback function that is called when the address of the given coordinates is fetched
     * @param address
     */
    onAddressOfCoordinatesFetched?: (address: MapboxAddress) => void;
    /**
     * If the map should animate to the starting location
     */
    animateToStartingLocation?: boolean;
    /**
     * Callback function that is called when the component mounts
     */
    startingAddress?: MapboxAddress;
    /**
     * The starting coordinates of the mapbox
     */
    startingCoordinates?: MapboxCoordinates;
    /**
     * The class name of the map container
     */
    mapContainerClassName?: string;
    /**
     * The zoom level of the mapbox
     */
    zoomLevel?: number;
    /**
     * The style of the map container
     */
    sx?: BoxProps['sx'],
    /**
     * Options of the mapbox
     */
    options?: MapboxOptions;
    /**
     * The markers of the mapbox
     */
    markers?: MapboxMarker[],
    /**
     * The circles of the mapbox
     */
    circles?: MapboxCircle[],
    /**
     * The controls of the mapbox
     */
    controls?: MapboxControls;
    /**
     * Fly to the marker on click
     */
    flyToMarkerOnClick?: boolean;
    /**
     * the color of the marker
     */
    markerColor?: string;
}

export interface MapboxRef {
    /**
     * Sets the address of the mapbox to the given address
     * @param address       The address to be searched
     * @param instance      The instance of the mapbox, if not given, the component's instance will be used
     * @param animate      If the map should animate to the given coordinates
     */
    setAddress: (address: MapboxAddress, instance?: mapboxgl.Map, animate?: boolean) => Promise<void>;
    /**
     * Sets the coordinates of the mapbox to the given coordinates
     * @param coordinates    The coordinates to be set
     * @param instance       The instance of the mapbox, if not given, the component's instance will be used
     * @param address        The address of the given coordinates. Only used internally
     * @param animate        If the map should animate to the given coordinates
     */
    setCoordinates: (coordinates: MapboxCoordinates, instance?: mapboxgl.Map, address?: MapboxAddress, animate?: boolean) => Promise<void>;
}

const Mapbox = forwardRef<MapboxRef, MapboxProps>((props, ref) => {
    const {onAddressOfCoordinatesFetched} = props;

    const [map, setMap] = useState<mapboxgl.Map>();
    const [mapLoaded, setMapLoaded] = useState(false);
    const isMounted = useIsMounted();

    const mapContainer = useRef<HTMLDivElement>(null);
    const mapRef = useRef(map);
    mapRef.current = map;

    /**
     * Awaits the map to load before continuing
     */
    const awaitMapLoad = useCallback(async (map: mapboxgl.Map) => {
        if (map.loaded()) {
            return Promise.resolve();
        }
        return new Promise<void>((resolve) => {
            map.once('load', () => resolve());
        });
    }, [])

    /**
     * Parses the returned data of mapbox address api
     * @param data      The data returned from mapbox
     * @returns         The parsed address
     */
    const parseMapboxAddress = useCallback((data: GetMapboxMarkerAddressResponseDS): MapboxAddress => {
        const foundAddress: MapboxAddress = {
            address: '',
            province: '',
            country: '',
            postalCode: '',
        };
        const addressSegments = {
            locality: '',
            neighborhood: '',
        }
        data.features.forEach((item) => {
            switch (item.place_type[0]) {
                case 'postcode': {
                    foundAddress.postalCode = item.text;
                    break;
                }
                case 'address': {
                    let streetName = item.text;
                    const lengthOfStreetNumber = item.place_name.indexOf(item.text);
                    if (lengthOfStreetNumber !== -1) {
                        streetName = item.place_name.substring(0, lengthOfStreetNumber) + streetName;
                    }
                    foundAddress.address = streetName;
                    break;
                }
                case 'poi': {
                    foundAddress.address = item.properties.address;
                    break;
                }
                case 'neighborhood': {
                    addressSegments.neighborhood = item.text;
                    break;
                }
                case 'locality': {
                    addressSegments.locality = item.text;
                    break;
                }
                case 'place': {
                    foundAddress.city = item.text;
                    break;
                }
                case 'region':
                    foundAddress.province = item.text;
                    break;
                case 'country':
                    foundAddress.country = item.text;
                    foundAddress.countryShortCode = item.properties.short_code;
                    break;
                default:
                    break;
            }
        });
        if (!foundAddress.address) {
            foundAddress.address = [addressSegments.locality, addressSegments.neighborhood].filter(e => !!e).join(', ');
        }
        return foundAddress;
    }, [])

    /**
     * Fetches the address based on the given coordinates
     * @param lng   The longitude of the marker
     * @param lat   The latitude of the marker
     */
    const fetchAddressFromCoordinates = useCallback(async (lng: number, lat: number): Promise<MapboxAddress | undefined> => {
        const response = await MapboxApiService.getMapboxMarkerAddress(lng, lat, mapboxgl.accessToken!);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            return parseMapboxAddress(response.data!);
        }
    }, [isMounted, parseMapboxAddress])


    /**
     * Fetches the coordinates of the given address
     * @param address    The address to be searched
     */
    const fetchCoordinatesFromAddress = useCallback(async (mapboxAddress: MapboxAddress) => {
        const address: MapboxStartingAddress = {
            address: [mapboxAddress.address ?? "", mapboxAddress?.province ?? ""].filter(e => !!e).join(', ').concat(mapboxAddress.postalCode ?? ""),
            country: mapboxAddress.countryShortCode ?? getCountryCode(mapboxAddress.country)?.toLowerCase() ?? "",
        }
        if (!address?.address?.length)
            return;
        const response = await MapboxApiService.getMapboxCoordinates(
            address?.address,
            mapboxgl.accessToken!,
            address.country,
        );
        if (!isMounted())
            return;
        if (!response?.resultFlag || !response?.data?.features?.length) {
            return;
        }
        return response.data.features[0].center;
    }, [isMounted])

    /**
     * Validates the given coordinates
     */
    const validateCoordinates = useCallback((coordinates: MapboxCoordinates) => {
        return !(!coordinates || !coordinates.lng || !coordinates.lat || isNaN(coordinates.lng) || isNaN(coordinates.lat));
    }, [])

    /**
     * Sets the coordinates of the mapbox to the given coordinates
     * @param coordinates    The coordinates to be set
     * @param instance      The instance of the mapbox, if not given, the component's instance will be used
     * @param address        The address of the given coordinates. Only used internally
     * @param animate      If the map should animate to the given coordinates
     */
    const setCoordinates = useCallback<MapboxRef['setCoordinates']>(async (coordinates, instance, address, animate = true) => {
        if (!validateCoordinates(coordinates))
            return;
        if (onAddressOfCoordinatesFetched) {
            if (address) {
                onAddressOfCoordinatesFetched(address);
            } else {
                const fetchedAddress = await fetchAddressFromCoordinates(coordinates.lng, coordinates.lat);
                if (!!fetchedAddress) {
                    onAddressOfCoordinatesFetched(fetchedAddress);
                }
            }
        }
        instance = instance ?? mapRef.current;
        if (!instance)
            return
        await awaitMapLoad(instance);
        instance.flyTo({
            center: [coordinates.lng, coordinates.lat],
            zoom: props.zoomLevel ?? 15,
            bearing: 0,
            animate: animate,
            essential: animate,
        });
    }, [props.zoomLevel, awaitMapLoad, fetchAddressFromCoordinates, onAddressOfCoordinatesFetched, validateCoordinates])

    /**
     * Sets the address of the mapbox to the given address
     * @param address       The address to be searched
     * @param instance      The instance of the mapbox, if not given, the component's instance will be used
     * @param animate     If the map should animate to the given coordinates
     */
    const setAddress = useCallback<MapboxRef['setAddress']>(async (address, instance, animate = true) => {
        const center = await fetchCoordinatesFromAddress(address);
        if (!center)
            return;
        await setCoordinates({lng: center[0], lat: center[1]}, instance, address, animate);
    }, [setCoordinates, fetchCoordinatesFromAddress]);

    /**
     * Attaches functions to the ref of the component to be used outside the component.
     */
    useImperativeHandle(ref, () => ({
        setAddress: setAddress,
        setCoordinates: setCoordinates,
    }), [setAddress, setCoordinates])

    /**
     * Creates a new MapboxGL and adds controls to it.
     */
    const createMapAndMapControls = useCallback((): mapboxgl.Map => {
        const newMap = new mapboxgl.Map({
            container: mapContainer.current!,
            style: 'mapbox://styles/mapbox/standard',
            center: [45, 45],
            zoom: 1,
            ...props.options,
        });
        if (props.options?.theme) {
            newMap.setConfigProperty('basemap', 'lightPreset', props.options.theme === 'light' ? 'day' : 'night');
        }
        if (props.controls?.navigation !== false) {
            newMap.addControl(new mapboxgl.NavigationControl(), 'bottom-left');
        }
        if (props.controls?.geolocate !== false) {
            newMap.addControl(new mapboxgl.GeolocateControl({positionOptions: {enableHighAccuracy: true}, trackUserLocation: true}), 'bottom-left');
        }
        if (props.controls?.geocoder !== false) {
            // @ts-ignore
            newMap.addControl(new MapboxGeocoder({accessToken: mapboxgl.accessToken!}), 'top-right');
        }
        if (props.controls?.scale !== false) {
            newMap.addControl(new mapboxgl.ScaleControl(), 'bottom-right');
        }
        return newMap;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    /**
     * Navigates to the starting position of the map
     */
    const navigateToStartingPosition = useCallback(() => {
        const animate = props.animateToStartingLocation ?? true;
        if (props.startingAddress) {
            void setAddress(props.startingAddress, mapRef.current, animate);
        } else if (props.startingCoordinates) {
            void setCoordinates(props.startingCoordinates, mapRef.current, undefined, animate);
        }
    }, [setAddress, props.startingAddress, props.startingCoordinates, setCoordinates, props.animateToStartingLocation]);

    /**
     * As soon as the component mounts:
     * - Creates the map and adds the search, zoomIn, zoomOut, and current functionalities
     * - If the starting address is given, sets the address of the mapbox to the given address
     * - If the starting coordinates are given, sets the coordinates of the mapbox to the given coordinates
     */
    useEffect(() => {
        if (!mapRef.current) {
            const map = createMapAndMapControls();
            setMap(map);
            mapRef.current = map;
        } else {
        }
        mapRef.current.once('load', () => setMapLoaded(true));
        mapRef.current.once('remove', () => setMapLoaded(false));
        navigateToStartingPosition();
    }, [createMapAndMapControls, navigateToStartingPosition])

    /**
     * With each change in the [options] of props:
     * - updates the map box's options
     */
    useEffect(() => {
        if (!map || !props.options?.theme)
            return;
        map.setConfigProperty('basemap', 'lightPreset', props.options.theme === 'light' ? 'day' : 'night');
    }, [props.options?.theme, map, mapLoaded]);

    /**
     * Resizes the map each time the window is resized.
     */
    useEffect(() => {
        let timer: number | undefined;
        const listener = debounce(() => {
            if (timer) {
                window.clearTimeout(timer);
                timer = undefined;
            }
            mapRef.current?.resize();
        }, 100);

        window.addEventListener('resize', listener);
        return () => window.removeEventListener('resize', listener);
    }, []);

    return (
        <ErrorBoundary fallback={<></>}>
            <Box className='mapbox'>
                <Box
                    ref={mapContainer}
                    sx={props.sx}
                    className={classnames('map-container', props.mapContainerClassName)}
                />
                {
                    mapLoaded &&
                    <MapboxCircles
                        map={map}
                        circles={props.circles}
                        sourceId={'circles'}
                        layerId={'circles'}
                        markerColor={props.markerColor}
                        flyToMarkerOnClick={props.flyToMarkerOnClick}
                    />
                }
                {
                    mapLoaded &&
                    <MapboxMarkers
                        map={map}
                        markers={props.markers}
                        markerColor={props.markerColor}
                        flyToMarkerOnClick={props.flyToMarkerOnClick}
                    />
                }
                {
                    mapLoaded &&
                    <MapboxInteractions
                        map={map}
                        fetchAddressFromCoordinates={fetchAddressFromCoordinates}
                        onCoordinatesSelect={props.onCoordinatesSelect}
                        onAddressSelect={props.onAddressSelect}
                        markerColor={props.markerColor}
                        flyToMarkerOnClick={props.flyToMarkerOnClick}
                        awaitMapLoad={awaitMapLoad}
                    />
                }
            </Box>
        </ErrorBoundary>
    )
});

export default Mapbox;
